'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/** Cian de firma del tema neón (kicker, outline del delivery). */
const CYAN = '#29d9d5'

/**
 * Hero del tema "neon" (nocturno eléctrico): letrero encendido a las 2 AM.
 * Fondo negro AZULADO con atmósfera de glow rosa/cian, nombre en Unbounded con
 * degradado rosa→cian (bg-clip) y glow SOLO en el CTA — la disciplina del tema
 * es un solo elemento encendido a la vez. La barra superior usa modo sólido
 * (navSolid). Lee tokens del tema (--ms-*).
 */
export function HeroNeon({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)
  const hasMedia = !!(heroImageUrl || heroVideoUrl)

  return (
    <section className="relative w-full overflow-hidden" style={{ background: 'var(--ms-bg)' }}>
      {/* Media a sangre (opcional) bajo el overlay del token */}
      {heroVideoUrl ? (
        <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" src={heroVideoUrl} />
      ) : heroImageUrl ? (
        <Image src={heroImageUrl} alt={businessName} fill priority sizes="100vw" className="object-cover" />
      ) : null}
      {hasMedia && <div className="absolute inset-0 z-10" style={{ background: 'var(--ms-hero-overlay)' }} />}

      {/* Atmósfera: glow rosa arriba-izquierda + cian abajo-derecha, tenues */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{ background: 'radial-gradient(70% 55% at 18% 0%, color-mix(in srgb, var(--ms-accent) 16%, transparent), transparent 60%)' }}
      />
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{ background: `radial-gradient(60% 50% at 88% 100%, color-mix(in srgb, ${CYAN} 12%, transparent), transparent 60%)` }}
      />

      {/* Contenido centrado */}
      <div
        className="relative z-20 mx-auto flex max-w-3xl flex-col items-center justify-center px-6 text-center"
        style={{ minHeight: 'min(660px, 86vh)' }}
      >
        {city && (
          <span
            className="text-[11px] font-bold uppercase tracking-[0.34em] pl-[0.34em] mb-6"
            style={{ color: CYAN }}
          >
            {city}
          </span>
        )}

        {/* Nombre en degradado rosa→cian; el drop-shadow rosa hace de "tubo" */}
        <h1
          className="uppercase leading-[1.06] tracking-[0.02em] text-[clamp(34px,6.5vw,68px)]"
          style={{
            fontFamily: 'var(--ms-font-display)',
            fontWeight: 600,
            background: `linear-gradient(92deg, var(--ms-accent) 8%, ${CYAN} 92%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 0 22px color-mix(in srgb, var(--ms-accent) 38%, transparent))',
          }}
        >
          {businessName}
        </h1>

        {tagline && (
          <p className="mt-5 max-w-xl text-[16px] sm:text-[17.5px] leading-relaxed" style={{ color: 'var(--ms-text-soft)' }}>
            {tagline}
          </p>
        )}

        {/* Meta discreta */}
        <div
          className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[12.5px] tracking-wide"
          style={{ color: 'var(--ms-text-muted)' }}
        >
          {statusLabel && (
            <span className="inline-flex items-center gap-2">
              <span
                className="w-[6px] h-[6px] rounded-full"
                style={{
                  background:
                    statusLabel.tone === 'open'
                      ? CYAN
                      : statusLabel.tone === 'soon'
                        ? '#f59e0b'
                        : 'var(--ms-text-muted)',
                  boxShadow:
                    statusLabel.tone === 'open'
                      ? `0 0 8px color-mix(in srgb, ${CYAN} 70%, transparent)`
                      : undefined,
                }}
              />
              {statusLabel.text}
            </span>
          )}
          {statusLabel && <span aria-hidden>·</span>}
          <span>{t('pickupAvailable')}</span>
          {pickupWaitMinutes ? (
            <>
              <span aria-hidden>·</span>
              <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span>
            </>
          ) : null}
        </div>

        {/* CTAs: rosa con glow (el ÚNICO elemento encendido) + outline cian */}
        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onViewMenu}
            className="inline-flex items-center px-9 py-4 text-[13.5px] font-bold uppercase tracking-[0.12em] transition hover:brightness-110 active:scale-[0.985]"
            style={{
              background: 'var(--ms-accent)',
              color: '#ffffff',
              borderRadius: 'var(--ms-radius)',
              boxShadow: '0 0 26px color-mix(in srgb, var(--ms-accent) 45%, transparent)',
            }}
          >
            {t('orderOnline')}
          </button>
          {hasDelivery && onDelivery && (
            <button
              type="button"
              onClick={onDelivery}
              className="inline-flex items-center px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.12em] transition hover:bg-white/5"
              style={{
                color: CYAN,
                borderRadius: 'var(--ms-radius)',
                border: `1px solid color-mix(in srgb, ${CYAN} 55%, transparent)`,
              }}
            >
              {t('delivery')}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}
